"use client";

import { useEffect, useId, useRef, useState } from "react";
import type { AcfText, SectionProps } from "@/components/page-builder/types";
import WpImage from "@/components/ui/WpImage";
import type { MaybeMedia } from "@/lib/media";
import { renderText } from "./text";

type Contributor = { image?: MaybeMedia; label?: AcfText; text?: AcfText };

type Fields = {
  pretitle?: AcfText;
  /** Plain text; *stars* highlight a phrase, new lines break. */
  title?: AcfText;
  text?: AcfText;
  contributors?: Contributor[] | false;
  /** Prompt shown in the panel before anything is open. */
  placeholder?: AcfText;
};

/**
 * Accordion Showcase (accordion-showcase.php, with accordion-showcase.js)
 *
 * Pale blue two column section. The left column carries the eyebrow, heading and
 * body copy, then a stack of contributor pills the visitor opens one at a time.
 * The right column is a tall panel that holds a prompt until a contributor is
 * open, and then that contributor's image with its label and description over it.
 *
 * The markup ships fully open (every description visible, every image in the
 * flow, no prompt) so the section reads before hydration. data-enhanced on the
 * section is what collapses it, so the interactive state only ever exists once
 * this is running: the descriptions fold away and the panel becomes a stack,
 * the prompt underneath and each image a cross-fading layer over it. Opening a
 * pill reveals its description and brings the matching image up in the panel;
 * clicking the open pill again closes everything back to the prompt.
 *
 * Each item's data-open (and each image's data-active) is only set once
 * enhanced, so their open and closed styles never apply to the static markup.
 */
export default function AccordionShowcase({
  pretitle,
  title,
  text,
  contributors,
  placeholder,
  section_id,
}: SectionProps<Fields>) {
  const fallbackId = useId();
  const detailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [enhanced, setEnhanced] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const rows = (Array.isArray(contributors) ? contributors : []).filter(
    (contributor) => contributor.label || contributor.text || contributor.image,
  );

  // The pills and their panels are wired together by id, so they need a base unique to the page.
  const uid = section_id || `das-governance-${fallbackId.replace(/:/g, "")}`;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEnhanced(true);
  }, []);

  // The detail is measured rather than left on auto so it can transition.
  useEffect(() => {
    if (!enhanced) {
      return;
    }

    detailRefs.current.forEach((detail, index) => {
      if (detail) {
        detail.style.height =
          index === open ? `${detail.scrollHeight}px` : "0px";
      }
    });
  }, [enhanced, open]);

  // A measured height goes stale when the copy rewraps, so the open row is
  // re-measured whenever the window changes width.
  useEffect(() => {
    if (open === null) {
      return;
    }

    const onResize = () => {
      const detail = detailRefs.current[open];

      if (detail) {
        detail.style.height = `${detail.scrollHeight}px`;
      }
    };

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  if (!title && !rows.length) {
    return null;
  }

  const highlight = "text-das-green";
  const isEnhanced = enhanced && rows.length > 0;

  return (
    <section
      className="js-governance group/showcase bg-[#f4f8fb] py-32 tablet:py-24 mobile:py-18"
      data-id={section_id || undefined}
      data-enhanced={isEnhanced}
      data-open={enhanced && open !== null}
    >
      <div className="shell shell-s">
        <div className="grid grid-cols-[46fr_54fr] items-start gap-16 small-desktop:gap-12 tablet:grid-cols-[1fr] tablet:gap-10">
          <div>
            {pretitle && (
              <p className="js-das-reveal mb-10 text-[12px] leading-[1.4] font-medium tracking-[0.22em] text-das-green uppercase mobile:mb-7 mobile:text-[11px]">
                {pretitle}
              </p>
            )}

            {title && (
              <h2
                className="js-das-reveal mb-6 text-[length:clamp(24px,3vw,36px)] leading-[1.1] font-semibold tracking-[-0.025em] text-brand-blue"
                data-reveal-delay="0.08"
              >
                {renderText(title, highlight)}
              </h2>
            )}

            {text && (
              <p
                className="js-das-reveal mb-8 text-[14px] leading-[1.8] font-normal text-[#5a6a7a]"
                data-reveal-delay="0.16"
              >
                {renderText(text, highlight)}
              </p>
            )}

            {rows.length > 0 && (
              <ul
                className="js-das-reveal m-0 flex list-none flex-col items-start gap-[7px] p-0"
                data-reveal-delay="0.26"
              >
                {rows.map((contributor, index) => {
                  const detailId = `${uid}-contributor-${index}`;
                  const isOpen = enhanced && open === index;

                  return (
                    <li
                      key={index}
                      className="group/item max-w-full"
                      data-index={index}
                      data-open={enhanced ? isOpen : undefined}
                    >
                      <button
                        type="button"
                        className="flex cursor-pointer items-center gap-3 rounded-[100px] border-0 bg-brand-blue/82 py-2.5 pr-5 pl-2.5 transition-[background-color] duration-400 ease-[ease] group-data-[open=true]/item:bg-brand-blue hover:bg-brand-blue focus-visible:bg-brand-blue"
                        aria-expanded={isOpen ? "true" : "false"}
                        aria-controls={detailId}
                        onClick={() => setOpen(open === index ? null : index)}
                      >
                        <span
                          className="flex size-6 shrink-0 items-center justify-center rounded-full border-[1.5px] border-white/32 text-white [transition:transform_0.28s_cubic-bezier(0.34,1.56,0.64,1),border-color_0.4s] group-data-[open=true]/item:border-das-green/70 group-data-[open=true]/item:[transform:rotate(45deg)]"
                          aria-hidden="true"
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                            focusable="false"
                          >
                            <line
                              x1="5"
                              y1="1.5"
                              x2="5"
                              y2="8.5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                            <line
                              x1="1.5"
                              y1="5"
                              x2="8.5"
                              y2="5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>

                        <span className="text-[14px] leading-[1.4] font-semibold tracking-[-0.01em] whitespace-nowrap text-white">
                          {contributor.label}
                        </span>
                      </button>

                      {contributor.text && (
                        <div
                          className="mt-2 mb-1 pl-[46px] group-data-[enhanced=true]/showcase:h-0 group-data-[enhanced=true]/showcase:overflow-hidden group-data-[enhanced=true]/showcase:[transition:height_0.22s_ease-out,opacity_0.22s_ease-out,margin_0.4s] group-data-[open=false]/item:m-0 group-data-[open=false]/item:opacity-0"
                          id={detailId}
                          ref={(element) => {
                            detailRefs.current[index] = element;
                          }}
                        >
                          <p className="m-0 text-[13px] leading-[1.65] font-normal text-[#5a6a7a]">
                            {contributor.text}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            className="js-das-reveal relative overflow-hidden rounded-[18px] bg-brand-blue/5 group-data-[enhanced=true]/showcase:min-h-[520px] tablet:group-data-[enhanced=true]/showcase:min-h-[420px] mobile:group-data-[enhanced=true]/showcase:min-h-[340px]"
            data-reveal-delay="0.2"
          >
            {placeholder && (
              <div className="pointer-events-none absolute top-0 left-0 hidden h-full w-full flex-col items-center justify-center gap-3 bg-[linear-gradient(145deg,rgba(8,41,67,0.03)_0%,rgba(8,41,67,0.07)_100%)] transition-opacity duration-350 ease-[ease] group-data-[enhanced=true]/showcase:flex group-data-[open=true]/showcase:opacity-0">
                <span
                  className="size-[7px] rounded-full bg-das-green opacity-50"
                  aria-hidden="true"
                ></span>

                <p className="m-0 text-[12px] leading-[1.4] font-normal tracking-[0.02em] text-brand-blue/33">
                  {placeholder}
                </p>
              </div>
            )}

            {rows.map((contributor, index) => (
              <div
                key={index}
                className="relative group-data-[enhanced=false]/showcase:[&+&]:mt-1 group-data-[enhanced=true]/showcase:absolute group-data-[enhanced=true]/showcase:top-0 group-data-[enhanced=true]/showcase:left-0 group-data-[enhanced=true]/showcase:m-0 group-data-[enhanced=true]/showcase:h-full group-data-[enhanced=true]/showcase:w-full group-data-[enhanced=true]/showcase:[transition:opacity_0.55s_cubic-bezier(0.16,1,0.3,1),transform_0.55s_cubic-bezier(0.16,1,0.3,1)] data-[active=false]:pointer-events-none data-[active=false]:opacity-0 data-[active=false]:[transform:scale(1.04)] data-[active=true]:[transform:scale(1)]"
                data-index={index}
                data-active={isEnhanced ? open === index : undefined}
              >
                {contributor.image && (
                  <WpImage
                    media={contributor.image}
                    className="block h-full w-full object-cover"
                    alt={contributor.label || ""}
                    sizes="(max-width: 1024px) 100vw, 1024px"
                  />
                )}

                <div className="absolute right-0 bottom-0 left-0 bg-[linear-gradient(to_top,rgba(8,41,67,0.8)_0%,rgba(8,41,67,0.25)_45%,rgba(8,41,67,0)_100%)] px-8 py-7 mobile:p-5">
                  {contributor.label && (
                    <p className="mb-1.5 text-[11px] leading-[1.4] font-medium tracking-[0.2em] text-das-green uppercase">
                      {contributor.label}
                    </p>
                  )}

                  {contributor.text && (
                    <p className="m-0 max-w-[320px] text-[14px] leading-[1.6] font-normal text-white/78">
                      {contributor.text}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
